import { useCallback } from 'react';

import { type AirActionTrackingLocation, useTrackDownloadedProxyAssets } from '@air/analytics';
import { Clips } from '@air/api';
import { useToasts } from '@air/provider-toast';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { DownloadFile, isS3DownloadUrlActive } from '~/utils/DownloadFile';
import { reportErrorToBugsnag } from '~/utils/ErrorUtils';

export type DownloadPrivateProxyAssetParams = {
  id: string;
  resolutionId: string;
  trackLocation: AirActionTrackingLocation;
};

/**
 * This hook returns a function that downloads a single private proxy asset.
 */
export function useDownloadPrivateProxyAsset() {
  const { showToast } = useToasts();
  const { trackDownloadedProxyAssets } = useTrackDownloadedProxyAssets();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const downloadPrivateProxyAsset = useCallback(
    async ({ id, resolutionId, trackLocation }: DownloadPrivateProxyAssetParams) => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      try {
        const { url } = await Clips.getDownloadUrl({ clipId: id, options: { altResId: resolutionId }, workspaceId });

        if (isS3DownloadUrlActive(url)) {
          DownloadFile(url);

          trackDownloadedProxyAssets({
            id,
            proxyId: resolutionId,
            location: trackLocation,
          });
        } else {
          showToast('One or more of the selected file downloads has expired.');
        }
      } catch (error) {
        showToast('There was an error downloading assets.');

        reportErrorToBugsnag({
          error,
          context: 'Failed to download private assets from selection menu',
          metadata: {
            data: {
              id,
              resolutionId,
              location: trackLocation,
            },
          },
        });
      }
    },
    [showToast, trackDownloadedProxyAssets, workspaceId],
  );

  return {
    downloadPrivateProxyAsset,
  };
}
